// FILE: src/components/admin/auditActions.js
// Kinds of audit-log entry for the audit-log filters (WP-7, matrix 5.8). The
// server filters on a comma list of actions (?action=A,B), so each kind is a
// list; a single action can still be chosen. Labels come from
// auditActionLabel (src/i18n/labels.js): the page never shows a raw name.
import { auditActionLabel } from "../../i18n/labels";

export const AUDIT_GROUPS = [
  {
    id: "sign-in",
    label: "Sign-in and sign-out",
    actions: [
      "LOGIN_SUCCESS",
      "LOGIN_FAILURE",
      "LOGIN_BLOCKED_LOCKED",
      "OTP_SENT",
      "LOGOUT",
      "SESSION_IDLE_TIMEOUT",
      "SESSION_REPLACED",
      "AUTH_FAILURE",
      "REAUTH_FAILURE"
    ]
  },
  {
    id: "failures",
    label: "Failed or refused sign-ins",
    actions: ["LOGIN_FAILURE", "LOGIN_BLOCKED_LOCKED", "AUTH_FAILURE", "REAUTH_FAILURE", "IDENTITY_CHALLENGE_FAILED"]
  },
  {
    id: "security",
    label: "Passwords, codes and security questions",
    actions: [
      "PASSWORD_RESET_REQUESTED",
      "PASSWORD_RESET",
      "PASSWORD_CHANGED",
      "PASSWORD_EXPIRED",
      "TOTP_SETUP_STARTED",
      "TOTP_SETUP_REJECTED",
      "TOTP_ENABLED",
      "TOTP_DISABLED",
      "TOTP_DISABLE_REJECTED",
      "CHALLENGE_ANSWERS_SET",
      "CHALLENGE_ANSWERS_CHANGED",
      "CHALLENGE_ANSWERS_CHANGE_REJECTED"
    ]
  },
  {
    id: "identity",
    label: "Identity checks during lessons",
    actions: ["IDENTITY_CHALLENGE_ISSUED", "IDENTITY_CHALLENGE_PASSED", "IDENTITY_CHALLENGE_FAILED"]
  },
  {
    id: "accounts",
    label: "Account changes by staff",
    actions: [
      "STUDENT_INVITED",
      "INVITE_RESENT",
      "INVITE_EMAIL_FAILED",
      "USER_ENROLLED",
      "ADMIN_USER_UPDATED",
      "USER_LOCKED",
      "USER_UNLOCKED",
      "ENROLMENT_EXTENDED",
      "UNTIMED_GRANTED",
      "UNTIMED_REVOKED"
    ]
  },
  {
    id: "learning",
    label: "Lessons, time and breaks",
    actions: [
      "LEARNING_SESSION_STARTED",
      "LEARNING_SESSION_ENDED",
      "BREAK_REQUIRED",
      "BREAK_STARTED",
      "BREAK_ENDED",
      "DAILY_LIMIT_REACHED",
      "COMPLETION_REJECTED_MIN_TIME",
      "CHAPTER_COMPLETED",
      "HOMELINK_COMPLETED",
      "RESOURCE_SCORE_RECORDED",
      "RESOURCE_FINAL_SCORE_IMAGE",
      "ENROLMENT_EXPIRED"
    ]
  },
  {
    id: "onboarding",
    label: "Registration and getting started",
    actions: [
      "USER_REGISTERED",
      "INVITE_ACCEPTED",
      "CONSENT_GIVEN",
      "PROFILE_UPDATED",
      "TUTORIAL_COMPLETED",
      "DOCUMENT_ACKNOWLEDGED",
      "SURVEY_SUBMITTED"
    ]
  },
  {
    id: "support",
    label: "Support and accommodations",
    actions: [
      "SUPPORT_TICKET_CREATED",
      "SUPPORT_TICKET_MESSAGE",
      "SUPPORT_TICKET_STATUS",
      "ACCOMMODATION_REQUESTED",
      "ACCOMMODATION_UPDATED"
    ]
  },
  {
    id: "records",
    label: "Records and exports",
    actions: ["RECORD_EXPORTED", "RECORDS_BULK_EXPORTED", "DAILY_HOURS_EXPORTED"]
  },
  {
    id: "content",
    label: "Lesson and curriculum changes",
    actions: [
      "RESOURCE_CREATED",
      "RESOURCE_UPDATED",
      "RESOURCE_DELETED",
      "CURRICULUM_CREATED",
      "CURRICULUM_UPDATED",
      "CURRICULUM_DELETED",
      "CURRICULUM_VERSION_CREATED",
      "CURRICULUM_VALIDATED",
      "CURRICULUM_APPROVED",
      "CURRICULUM_RETIRED"
    ]
  },
  {
    id: "staff",
    label: "Staff, roles and refused requests",
    actions: ["STAFF_INVITED", "STAFF_ROLE_CHANGED", "ROLE_CREATED", "ROLE_UPDATED", "ROLE_DELETED", "PERMISSION_DENIED"]
  }
];

const ACTION_RE = /^[A-Z][A-Z0-9_]{1,63}$/;

/** Every single action the filter offers, sorted by its words. */
export const AUDIT_ACTIONS = Array.from(new Set(AUDIT_GROUPS.flatMap((g) => g.actions)))
  .map((value) => ({ value, label: auditActionLabel(value) }))
  .sort((a, b) => a.label.localeCompare(b.label));

/** The filter value ("group:sign-in" or "LOGIN_FAILURE") → the server's action list ("A,B"), or "". */
export function actionsForFilter(value) {
  if (!value) return "";
  if (value.startsWith("group:")) {
    const group = AUDIT_GROUPS.find((g) => `group:${g.id}` === value);
    return group ? group.actions.join(",") : "";
  }
  return ACTION_RE.test(value) ? value : "";
}

/** True when the URL's filter value is one the page offers (or a well-formed action name). */
export function isActionFilter(value) {
  return !!actionsForFilter(value);
}

export const OUTCOMES = ["SUCCESS", "FAILURE", "INFO"];
